import type {
  FullPublicPotentialProfileFragment,
  FullPublicProfileFragment,
  FullPublicProfileProductFragment,
} from 'apollo-gql'
import { pushToDataLayer } from 'lib/tracking'
import getProfileAnalyticsProperties from 'lib/tracking/avo/utils/getProfileAnalyticsProperties'
import getProfileProductAnalyticsProperties from 'lib/tracking/avo/utils/getProfileProductAnalyticsProperties'

import { ProfileViewedProperties } from './sdk'
import getMarketTrackingProperties from './utils/getMarketTrackingProperties'

interface TrackProfileViewedParameters {
  profile: FullPublicProfileFragment
  profileProduct?: FullPublicProfileProductFragment
  productSlug: string | string[]
}

const buildTrackingEventProperties = (
  parameters: TrackProfileViewedParameters,
): ProfileViewedProperties => {
  const { profile, profileProduct, productSlug } = parameters
  const productName = `${profile.slug} - ${productSlug}`

  return {
    ...getProfileAnalyticsProperties(profile),
    ...getProfileProductAnalyticsProperties(profileProduct),
    ...getMarketTrackingProperties(),
    productName,
    productSlug: `${productSlug}`,
  }
}

export const trackProfileViewed = async (
  parameters: TrackProfileViewedParameters,
): Promise<void> => {
  const trackingEventProperties: ProfileViewedProperties =
    buildTrackingEventProperties(parameters)

  const { profile, profileProduct } = parameters

  const { profileViewed } = await import('./sdk')

  profileViewed(trackingEventProperties)

  if (profile && profileProduct) {
    pushToDataLayer({
      event: 'productDetails',
      ecommerce: {
        currencyCode: profileProduct.price.currency,
        detail: {
          products: [
            {
              name: `${profile.slug} - ${profileProduct.product.slug}`,
              id: profileProduct.id,
              price: profileProduct.price.valueWithDecimals,
              category: profileProduct.product.productType,
            },
          ],
        },
      },
    })
  }

  if (profile) {
    pushToDataLayer({
      event: 'profileView',
      profileSlug: profile.slug,
      profileId: profile.id,
    })
  }
}

interface TrackPotentialProfileViewedParameters {
  potentialProfile: FullPublicPotentialProfileFragment
}

export const trackPotentialProfileViewed = async (
  parameters: TrackPotentialProfileViewedParameters,
): Promise<void> => {
  const { potentialProfile } = parameters

  const { profileViewed } = await import('./sdk')
  profileViewed({
    ...getProfileAnalyticsProperties(potentialProfile),
    ...getProfileProductAnalyticsProperties(),
    ...getMarketTrackingProperties(),
    productName: null,
    productSlug: null,
  })

  pushToDataLayer({
    event: 'profileView',
    profileSlug: potentialProfile.slug,
    profileId: potentialProfile.id,
  })
}
